import { memo, useEffect, useRef } from "react";
import { Card, Typography } from "antd";
import { motion } from "motion/react";
import { useAssetById } from "hooks/useAssetById";
import { StreamingText } from "./StreamingText";
import { MediaRender } from "./MediaRender";
import { getAssetUrl } from "./utils";
import { ANIMATION } from "./animationConfig";
import { useAnimationSequence } from "./useAnimationSequence";
import type { SlideComponentProps } from "./SlideRenderer";
import { TitlePlaceholders } from "types/";

export const TitleSlide = memo(
  ({ slide, isActive = false, onComplete }: SlideComponentProps) => {
    const placeholders = slide.placeholders as TitlePlaceholders;
    const title = placeholders.title;
    const subtitle = placeholders.subtitle;
    const imageElement = placeholders.image_element;
    const hasCalledComplete = useRef(false);

    const { data: imageAsset, isLoading } = useAssetById({
      assetId: imageElement?.asset_id,
    });

    const imageUrl = getAssetUrl(imageAsset?.data?.path);

    // Steps: 0=title, 1=subtitle, 2=image (if exists)
    const totalSteps = imageElement ? 3 : 2;
    const { nextStep, isStepActive, isComplete } = useAnimationSequence(
      totalSteps,
      isActive
    );

    // Notify parent when complete (once)
    useEffect(() => {
      if (isComplete && onComplete && !hasCalledComplete.current) {
        hasCalledComplete.current = true;
        onComplete();
      }
    }, [isComplete, onComplete]);

    // Not active yet - don't render animated content
    if (!isActive) {
      return (
        <Card>
          <Typography.Title level={3}>{title}</Typography.Title>
          <Typography.Text type="secondary">{subtitle}</Typography.Text>
          {imageElement && (
            <MediaRender
              imageElement={imageElement}
              imageUrl={imageUrl}
              imageAsset={imageAsset}
              isLoading={isLoading}
            />
          )}
        </Card>
      );
    }

    return (
      <Card>
        {/* Step 0: Title */}
        {isStepActive(0) && (
          <Typography.Title level={3}>
            <StreamingText
              text={title}
              animate
              wordDelay={ANIMATION.WORD_DELAY}
              onComplete={nextStep}
            />
          </Typography.Title>
        )}

        {/* Step 1: Subtitle */}
        {isStepActive(1) && (
          <Typography.Text type="secondary">
            <StreamingText
              text={subtitle}
              animate
              wordDelay={ANIMATION.WORD_DELAY}
              onComplete={nextStep}
            />
          </Typography.Text>
        )}

        {/* Step 2: Image (if exists) */}
        {imageElement && isStepActive(2) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onAnimationComplete={nextStep}
          >
            <MediaRender
              imageElement={imageElement}
              imageUrl={imageUrl}
              imageAsset={imageAsset}
              isLoading={isLoading}
            />
          </motion.div>
        )}
      </Card>
    );
  }
);

TitleSlide.displayName = "TitleSlide";
